import { useTranslation } from 'react-i18next'
import { useAttendanceHistory } from '@/hooks/learner/useAttendanceHistory'
import { AttendanceHistoryList, type AttendanceHistoryLabels } from '@/components/attendance/AttendanceHistoryList'
import { AttendanceCalendar } from './AttendanceCalendar'

const CARD_CLASS = 'rounded-2xl bg-lc-bg-primary p-6 shadow-lc-v3-card'
const HEADING_ID = 'attendance-history-heading'

export function AttendanceHistoryCard() {
  const { t } = useTranslation('learner')
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAttendanceHistory()

  const items = data?.pages.flatMap((p) => p.items) ?? []

  const labels: AttendanceHistoryLabels = {
    present: t('profile.attendance.status.present'),
    absent: t('profile.attendance.status.absent'),
    leave: t('profile.attendance.status.leave'),
    checkIn: (time) => t('profile.attendance.checkIn', { time }),
    checkOut: (time) => t('profile.attendance.checkOut', { time }),
  }

  return (
    <section className={CARD_CLASS} aria-labelledby={HEADING_ID}>
      <h2 id={HEADING_ID} className="mb-4 text-lc-v3-h5 font-bold text-lc-v3-gray-950">
        {t('profile.section.attendance')}
      </h2>
      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-5 w-full rounded bg-lc-gray-200 animate-pulse" />
          ))}
        </div>
      )}
      {isError && <p className="text-lc-body-3 text-lc-gray-700 py-6">{t('profile.attendance.error')}</p>}
      {!isLoading && !isError && items.length === 0 && (
        <p className="text-lc-body-3 text-lc-gray-600 py-6 text-center">{t('profile.attendance.empty')}</p>
      )}
      {!isLoading && !isError && items.length > 0 && (
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <AttendanceCalendar items={items} />
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <h3 className="px-2 text-lc-v3-body-1 font-bold text-lc-v3-gray-900">
              {t('profile.attendance.recordsTitle')}
            </h3>
            <AttendanceHistoryList
              items={items}
              labels={labels}
              className="max-h-[330px] overflow-y-auto rounded-xl border border-lc-disabled-border"
              rowClassName="px-4 py-3 odd:bg-lc-bg-primary even:bg-lc-v3-gray-50"
            />
            {hasNextPage && (
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full rounded-full bg-lc-v3-gray-100 px-4 py-2 text-lc-v3-caption-1 font-semibold text-lc-text-secondary hover:bg-lc-v3-gray-200 disabled:cursor-wait disabled:text-lc-text-disabled"
              >
                {t('profile.attendance.loadMore')}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
